import { takeLatest, put, call, fork, select } from 'redux-saga/effects';
import callApi from 'utils/api';
import * as actionTypes from './constants';
import {
  addMasterDataFailure,
  addMasterDataSuccess,
  deleteMasterDataFailure,
  deleteMasterDataSuccess,
  fetchMasterDataFailure,
  fetchMasterDataSuccess,
  fetchPlantsFailure,
  fetchPlantsSuccess,
  fetchSkillFailure,
  fetchSkillsFailure,
  fetchSkillsNextFailure,
  fetchSkillsNextSuccess,
  fetchSkillsSuccess,
  fetchSkillSuccess,
  fetchWizardDataFailure,
  fetchWizardDataSuccess,
  updateMasterDataFailure,
  updateMasterDataSuccess,
  updateSkillFailure,
  updateSkillSuccess,
} from './skills-actions';

export function* doFetchPlants(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'refdata/lookup/workcenters',
      options: {
        errorMessage: 'Could not fetch plants',
        retryAction: action,
      },
    });
    yield put(fetchPlantsSuccess(results));
  } catch (err) {
    yield put(fetchPlantsFailure(err));
  }
}

export function* doFetchSkills(action) {
  try {
    let urlString = '';
    if (action.payload) {
      const { plantId, regionId, technicianId } = action.payload;
      // eslint-disable-next-line max-len
      urlString = `?plantId=${plantId}${regionId ? `&regionId=${regionId}` : ''}${technicianId ? `&technicianId=${technicianId}` : ''}`;
    }
    const results = yield call(callApi, {
      endpoint: `skills/technician${urlString}`,
      options: {
        errorMessage: 'Could not fetch skills',
        retryAction: action,
      },
    });
    yield put(fetchSkillsSuccess(results));
  } catch (err) {
    yield put(fetchSkillsFailure(err));
  }
}

export function* doFetchNextSkills(action) {
  const { uri, query } = yield select(state => state.skills.skills.next);
  const { page, pageSize, plantId, regionId, technicianId } = query;
  try {
    const results = yield call(callApi, {
      // eslint-disable-next-line max-len
      endpoint: `${uri.replace('api/', '')}?page=${page}&pageSize=${pageSize}${plantId ? `&${plantId}` : ''}${regionId ? `&${regionId}` : ''}${technicianId ? `&${technicianId}` : ''}`,
      options: {
        errorMessage: 'Could not fetch skills',
        retryAction: action,
      },
    });
    yield put(fetchSkillsNextSuccess(results));
  } catch (err) {
    yield put(fetchSkillsNextFailure(err));
  }
}

export function* doUpdateSkill(action) {
  const { id, originalSkills, assignedSkills } = action.payload;
  const body = {
    assignedSkills,
  };
  try {
    const results = yield call(callApi, {
      endpoint: `skills/technician/${id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(body),
        errorMessage: `Could not update skills for ${id}`,
        retryAction: action,
      },
    });
    yield put(updateSkillSuccess(results));
  } catch (err) {
    yield put(updateSkillFailure({ id, originalSkills, ...err }));
  }
}

export function* doFetchWizardData(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'skills/wizard',
      options: {
        errorMessage: 'Could not fetch wizard data',
        retryAction: action,
      },
    });
    yield put(fetchWizardDataSuccess(results));
  } catch (err) {
    yield put(fetchWizardDataFailure(err));
  }
}

export function* doFetchMasterData(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'skills',
      options: {
        errorMessage: 'Could not fetch master data',
        retryAction: action,
      },
    });
    yield put(fetchMasterDataSuccess(results));
  } catch (err) {
    yield put(fetchMasterDataFailure(err));
  }
}

export function* doUpdateMasterData(action) {
  const { id } = action.payload;
  try {
    const results = yield call(callApi, {
      endpoint: `skills/${id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify(action.payload),
        errorMessage: 'Could not update master data',
        retryAction: action,
      },
    });
    yield put(updateMasterDataSuccess(results));
  } catch (err) {
    yield put(updateMasterDataFailure(err));
  }
}

export function* doAddMasterData(action) {
  try {
    const results = yield call(callApi, {
      endpoint: 'skills',
      options: {
        method: 'POST',
        body: JSON.stringify(action.payload),
        errorMessage: 'Could not add master data',
        retryAction: action,
      },
    });
    yield put(addMasterDataSuccess(results));
  } catch (err) {
    yield put(addMasterDataFailure(err));
  }
}

export function* doDeleteMasterData(action) {
  const { id } = action.payload;
  try {
    const results = yield call(callApi, {
      endpoint: `skills/${id}`,
      options: {
        method: 'DELETE',
        errorMessage: 'Could not delete skill',
        retryAction: action,
      },
    });
    yield put(deleteMasterDataSuccess(results));
  } catch (err) {
    yield put(deleteMasterDataFailure(err));
  }
}

export function* doFetchSkill(action) {
  try {
    const results = yield call(callApi, {
      endpoint: `skills/${action.payload}`,
      options: {
        errorMessage: 'Could not fetch skill data',
        retryAction: action,
      },
    });
    yield put(fetchSkillSuccess(results));
  } catch (err) {
    yield put(fetchSkillFailure(err));
  }
}

function* watchFetchPlants() {
  yield takeLatest(actionTypes.FETCH_SKILL_PLANTS_REQUEST, doFetchPlants);
}

function* watchFetchSkills() {
  yield takeLatest(actionTypes.FETCH_SKILLS_REQUEST, doFetchSkills);
}

function* watchFetchNextSkills() {
  yield takeLatest(actionTypes.FETCH_SKILLS_NEXT_REQUEST, doFetchNextSkills);
}

function* watchUpdateSkills() {
  yield takeLatest(actionTypes.UPDATE_SKILL_REQUEST, doUpdateSkill);
}

function* watchFetchWizardData() {
  yield takeLatest(actionTypes.FETCH_WIZARD_DATA_REQUEST, doFetchWizardData);
}

function* watchFetchMasterData() {
  yield takeLatest(actionTypes.FETCH_MASTER_DATA_REQUEST, doFetchMasterData);
}

function* watchUpdateMasterData() {
  yield takeLatest(actionTypes.UPDATE_MASTER_DATA_REQUEST, doUpdateMasterData);
}

function* watchAddMasterData() {
  yield takeLatest(actionTypes.ADD_MASTER_DATA_REQUEST, doAddMasterData);
}

function* watchDeleteMasterData() {
  yield takeLatest(actionTypes.DELETE_MASTER_DATA_REQUEST, doDeleteMasterData);
}

function* watchFetchSkill() {
  yield takeLatest(actionTypes.FETCH_SKILL_REQUEST, doFetchSkill);
}

export default [
  fork(watchFetchPlants),
  fork(watchFetchSkills),
  fork(watchFetchNextSkills),
  fork(watchUpdateSkills),
  fork(watchFetchWizardData),
  fork(watchFetchMasterData),
  fork(watchUpdateMasterData),
  fork(watchAddMasterData),
  fork(watchDeleteMasterData),
  fork(watchFetchSkill),
];
